'use client';
import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { usePreferences } from '@/contexts/PreferencesContext';

interface PortalLayoutProps {
  children: React.ReactNode;
  role?: 'student' | 'staff' | 'coordinator' | 'executive' | 'sponsor' | 'admin' | 'reporting' | 'finance';
  pageTitle?: string;
  breadcrumb?: string[];
}

export default function PortalLayout({ children, role, pageTitle, breadcrumb }: PortalLayoutProps) {
  const { prefs, loading: prefsLoading } = usePreferences();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [prefApplied, setPrefApplied] = useState(false);

  // Apply sidebar_collapsed preference once prefs are loaded
  useEffect(() => {
    if (!prefsLoading && !prefApplied) {
      setSidebarOpen(!prefs.display_sidebar_collapsed);
      setPrefApplied(true);
    }
  }, [prefsLoading, prefs.display_sidebar_collapsed, prefApplied]);

  // Apply compact table class to body
  useEffect(() => {
    if (prefs.display_compact_tables) {
      document.body.classList.add('compact-tables');
    } else {
      document.body.classList.remove('compact-tables');
    }
  }, [prefs.display_compact_tables]);

  return (
    <ErrorBoundary>
      {/* Skip to main content — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg focus:text-sm focus:font-600 focus:shadow-lg focus:outline-none"
      >
        Skip to main content
      </a>

      <div className="flex h-screen overflow-hidden bg-background">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar — navigation landmark */}
        <div
          className={`
            fixed lg:relative z-50 lg:z-auto h-full
            transition-sidebar
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            ${sidebarOpen ? 'w-64' : 'w-16'}
          `}
          aria-hidden={!mobileOpen && typeof window !== 'undefined' && window.innerWidth < 1024 ? true : undefined}
        >
          <Sidebar
            role={role}
            collapsed={!sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Header
            pageTitle={pageTitle}
            breadcrumb={breadcrumb}
            role={role}
            onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
          />
          <main
            id="main-content"
            className="flex-1 overflow-y-auto p-4 lg:p-6 xl:p-8"
            tabIndex={-1}
          >
            <div className="max-w-screen-2xl mx-auto">
              {children}
            </div>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
}